import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import jarasnaBell from "../../assets/jarasna-bell.png";
import { useAuth } from "./auth-context";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn, requestPasswordReset, verifyRecoveryCode } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [recoveryRequested, setRecoveryRequested] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    if (forgotMode && recoveryRequested) {
      const error = await verifyRecoveryCode(email, recoveryCode);
      if (error) setMessage(t("auth.recoveryCodeInvalid"));
      setBusy(false);
      return;
    }
    const error = forgotMode ? await requestPasswordReset(email) : await signIn(email, password);
    if (forgotMode && !error) setRecoveryRequested(true);
    const signInMessage = error === "invalid_credentials"
      ? "auth.invalidCredentials"
      : error === "email_not_confirmed"
        ? "auth.emailNotConfirmed"
        : error === "rate_limited"
          ? "auth.signInRateLimited"
          : "auth.genericError";
    setMessage(error
      ? t(error === "email_rate_limit" ? "auth.resetRateLimited" : signInMessage)
      : forgotMode ? t("auth.resetSent") : null);
    setBusy(false);
  };

  return (
    <main className="auth-page">
      <section className="auth-card" aria-labelledby="auth-title">
        <div className="auth-actions"><LanguageSwitcher /></div>
        <div className="brand auth-brand"><span className="auth-logo"><img alt={t("app.logoAlt")} src={jarasnaBell}/></span><span className="brand-copy"><strong>{t("app.name")}</strong><small>{t("app.tagline")}</small></span></div>
        <p className="eyebrow">{t("auth.internalOnly")}</p>
        <h1 id="auth-title">{forgotMode ? t("auth.resetTitle") : t("auth.signInTitle")}</h1>
        <p className="lead">{forgotMode ? t("auth.resetDescription") : t("auth.signInDescription")}</p>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="email">{t("auth.email")}</label>
          <input autoCapitalize="none" autoComplete="email" autoCorrect="off" disabled={forgotMode && recoveryRequested} id="email" inputMode="email" name="email" required spellCheck={false} type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          {!forgotMode && <>
            <label htmlFor="password">{t("auth.password")}</label>
            <div className="password-input-row">
              <input autoCapitalize="none" autoComplete="current-password" autoCorrect="off" id="password" minLength={12} name="password" required spellCheck={false} type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} />
              <button aria-controls="password" aria-pressed={showPassword} className="password-visibility-button" onClick={() => setShowPassword((value) => !value)} type="button">
                {t(showPassword ? "auth.hidePassword" : "auth.showPassword")}
              </button>
            </div>
          </>}
          {forgotMode && recoveryRequested && <>
            <label htmlFor="recovery-code">{t("auth.recoveryCode")}</label>
            <input autoComplete="one-time-code" autoFocus id="recovery-code" inputMode="numeric" maxLength={6} minLength={6} pattern="[0-9]{6}" required value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value.replace(/\D/g, "").slice(0, 6))}/>
            <p className="field-help">{t("auth.recoveryCodeHelp")}</p>
          </>}
          {message && <p className="form-message" role="status">{message}</p>}
          <button className="primary-button" disabled={busy} type="submit">
            {busy ? t("auth.working") : forgotMode ? t(recoveryRequested ? "auth.verifyRecoveryCode" : "auth.sendReset") : t("auth.signIn")}
          </button>
          {forgotMode && recoveryRequested && <button className="link-button" type="button" onClick={() => { setRecoveryRequested(false); setRecoveryCode(""); setMessage(null); }}>{t("auth.changeRecoveryEmail")}</button>}
          <button className="link-button" type="button" onClick={() => { setForgotMode(!forgotMode); setRecoveryRequested(false); setRecoveryCode(""); setMessage(null); }}>
            {forgotMode ? t("auth.backToSignIn") : t("auth.forgotPassword")}
          </button>
        </form>
      </section>
    </main>
  );
}
